/**
 * BW Product Drawers
 * Flyout drawer with nested accordion, driven by metaobjects.
 * One BwProductDrawers instance per section.
 */

(function () {
  'use strict';

  class BwProductDrawers {
    constructor(el) {
      this.el        = el;
      this.sid       = el.dataset.sectionId;
      this.drawer    = el.querySelector('#bw-drawer-' + this.sid) || document.getElementById('bw-drawer-' + this.sid);
      this.overlay   = el.querySelector('#bw-drawer-overlay-' + this.sid) || document.getElementById('bw-drawer-overlay-' + this.sid);
      this.body      = this.drawer ? this.drawer.querySelector('#bw-drawer-body-' + this.sid) : null;
      this.title     = this.drawer ? this.drawer.querySelector('#bw-drawer-title-' + this.sid) : null;
      this.closeBtn  = this.drawer ? this.drawer.querySelector('.bw-drawer__close') : null;
      this.triggers  = Array.from(el.querySelectorAll('.bw-drawers__btn'));
      this.isOpen    = false;

      // Move drawer and overlay to body to prevent position: fixed issues with transformed parents
      if (this.drawer && this.drawer.parentNode !== document.body) {
        document.body.appendChild(this.drawer);
      }
      if (this.overlay && this.overlay.parentNode !== document.body) {
        document.body.appendChild(this.overlay);
      }

      this._onKeyDown = this._onKeyDown.bind(this);
      this._bindEvents();
    }

    /* ── Public ──────────────────────────────────────────── */

    open(index) {
      const panel = document.getElementById('bw-panel-' + this.sid + '-' + index);
      if (!panel) return;

      // Clone pre-rendered panel into drawer body
      const clone = panel.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('style');

      this.body.innerHTML = '';
      this.body.appendChild(clone);
      this.body.scrollTop = 0;

      // Set title
      this.title.textContent = panel.dataset.label || '';

      // Update trigger aria states
      this.triggers.forEach((btn, i) => {
        btn.setAttribute('aria-expanded', i === index ? 'true' : 'false');
      });

      // Animate in
      this.drawer.classList.add('bw-drawer--active');
      this.overlay.classList.add('bw-drawer-overlay--active');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.isOpen = true;

      // Keyboard handler
      document.addEventListener('keydown', this._onKeyDown);

      // Focus close button after transition settles
      setTimeout(() => {
        this.closeBtn.focus({ preventScroll: true });
      }, 420);
    }

    close() {
      if (!this.isOpen) return;

      this.drawer.classList.remove('bw-drawer--active');
      this.overlay.classList.remove('bw-drawer-overlay--active');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.isOpen = false;

      // Reset all trigger aria states
      this.triggers.forEach(btn => btn.setAttribute('aria-expanded', 'false'));

      document.removeEventListener('keydown', this._onKeyDown);

      // Clear body content after slide-out transition
      setTimeout(() => {
        if (!this.isOpen) this.body.innerHTML = '';
      }, 420);
    }

    /* ── Private ─────────────────────────────────────────── */

    _bindEvents() {
      // Trigger buttons
      this.triggers.forEach(btn => {
        btn.addEventListener('click', () => {
          const index = parseInt(btn.dataset.drawerIndex, 10);
          this.open(index);
        });
      });

      // Close button
      this.closeBtn.addEventListener('click', () => this.close());

      // Overlay click
      this.overlay.addEventListener('click', () => this.close());

      // Accordion inside drawer — event delegation on body
      this.body.addEventListener('click', (e) => {
        const header = e.target.closest('.bw-di__header');
        if (header) this._toggleItem(header);
      });
    }

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }

      // Basic focus trap — Tab / Shift+Tab
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        this.drawer.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.closest('[aria-hidden="true"]'));

      if (!focusable.length) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    _toggleItem(header) {
      const item = header.closest('.bw-di');
      const bodyEl = item.querySelector('.bw-di__body');
      if (!bodyEl) return;
      
      const isOpen = item.classList.contains('bw-di--open');

      if (isOpen) {
        // Close current
        item.classList.remove('bw-di--open');
        header.setAttribute('aria-expanded', 'false');
        bodyEl.style.maxHeight = '0';
      } else {
        // Close all other items in this drawer first
        const allItems = this.body.querySelectorAll('.bw-di');
        allItems.forEach(otherItem => {
          if (otherItem !== item && otherItem.classList.contains('bw-di--open')) {
            otherItem.classList.remove('bw-di--open');
            const otherHeader = otherItem.querySelector('.bw-di__header');
            const otherBody = otherItem.querySelector('.bw-di__body');
            if (otherHeader) otherHeader.setAttribute('aria-expanded', 'false');
            if (otherBody) otherBody.style.maxHeight = '0';
          }
        });

        // Open target — measure scrollHeight first, then set
        bodyEl.style.maxHeight = bodyEl.scrollHeight + 'px';
        item.classList.add('bw-di--open');
        header.setAttribute('aria-expanded', 'true');
      }
    }
  }

  /* ── Init ───────────────────────────────────────────────── */

  function initAll() {
    document.querySelectorAll('.bw-drawers').forEach(el => {
      if (!el.dataset.bwDrawersInit) {
        el.dataset.bwDrawersInit = '1';
        new BwProductDrawers(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor re-init on section reload
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('.bw-drawers');
    if (el) {
      delete el.dataset.bwDrawersInit;
      new BwProductDrawers(el);
    }
  });

})();
